import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, Scissors, FileText, X } from 'lucide-react';

interface TextSelectionOverlayProps {
  selectedText: string;
  isVisible: boolean;
  onCopy: () => void;
  onExtract: () => void;
  onClear: () => void;
  position?: { x: number; y: number };
  className?: string;
}

const TextSelectionOverlay: React.FC<TextSelectionOverlayProps> = ({
  selectedText,
  isVisible,
  onCopy,
  onExtract,
  onClear,
  position,
  className = ""
}) => {
  const [overlayPosition, setOverlayPosition] = useState({ x: 0, y: 0 });
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    if (selectedText) {
      const words = selectedText.trim().split(/\s+/).filter(word => word.length > 0);
      setWordCount(words.length);
      setCharCount(selectedText.length);
    } else {
      setWordCount(0);
      setCharCount(0);
    }
  }, [selectedText]);

  useEffect(() => {
    if (isVisible && !position) {
      // Auto-position near selection
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        
        setOverlayPosition({
          x: Math.min(rect.left, window.innerWidth - 300),
          y: Math.max(rect.top - 80, 10)
        });
      }
    } else if (position) {
      setOverlayPosition(position);
    }
  }, [isVisible, position]);

  if (!isVisible || !selectedText) {
    return null;
  }

  const truncatedText = selectedText.length > 50 
    ? selectedText.substring(0, 50) + '...' 
    : selectedText;

  return (
    <Card
      className={`fixed z-50 p-3 shadow-lg border bg-background/95 backdrop-blur-sm min-w-[280px] max-w-[400px] ${className}`}
      style={{
        left: overlayPosition.x,
        top: overlayPosition.y,
        transform: 'translateY(-100%)'
      }}
    >
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Text Selected</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {wordCount} word{wordCount !== 1 ? 's' : ''}
          </Badge>
          <Badge variant="secondary" className="text-xs">
            {charCount} char{charCount !== 1 ? 's' : ''}
          </Badge>
        </div>

        {/* Preview */}
        <div className="bg-muted/50 p-2 rounded-md">
          <p className="text-xs text-muted-foreground leading-relaxed">
            "{truncatedText}"
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={onCopy}
            className="flex-1"
          >
            <Copy className="h-3 w-3 mr-1" />
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onExtract}
            className="flex-1"
          >
            <Scissors className="h-3 w-3 mr-1" />
            Extract
          </Button>
        </div>

        {/* Quick tip */}
        <div className="text-xs text-muted-foreground">
          Tip: Use Ctrl+C to copy or Ctrl+A to select all
        </div>
      </div>
    </Card>
  );
};

export default TextSelectionOverlay;